export class ServiceWorkerSecurity {
  private static instance: ServiceWorkerSecurity;
  private allowedOrigins: Set<string>;
  private blockedAttempts: Map<string, number> = new Map();

  private constructor() {
    this.allowedOrigins = new Set([
      window.location.origin,
      'https://incrementalhealth.test',
      'https://cet-demo.paperscissors.me',
      'https://api.cenextra.org',
      'https://cenextra.org',
      'https://cenextra.com',
      'http://incrementalhealth.test:5173',
      'http://incrementalhealth.test:5174',
      'http://localhost:5173',
      'http://localhost:5174',
    ]);

    this.interceptServiceWorkerRegistration();
    this.monitorServiceWorkers();
  }

  static getInstance(): ServiceWorkerSecurity {
    if (!ServiceWorkerSecurity.instance) {
      ServiceWorkerSecurity.instance = new ServiceWorkerSecurity();
    }
    return ServiceWorkerSecurity.instance;
  }

  private interceptServiceWorkerRegistration(): void {
    if (!('serviceWorker' in navigator)) return;

    const originalRegister = navigator.serviceWorker.register;

    navigator.serviceWorker.register = async (
      scriptURL: string | URL,
      options?: RegistrationOptions
    ) => {
      const url =
        typeof scriptURL === 'string' ? scriptURL : scriptURL.toString();

      try {
        const scriptOrigin = new URL(url, window.location.origin).origin;

        if (!this.isOriginAllowed(scriptOrigin)) {
          this.logBlockedAttempt(scriptOrigin, url);
          throw new Error(
            `ServiceWorker registration blocked: Unauthorized origin ${scriptOrigin}`
          );
        }

        if (!this.isScriptPathSafe(url)) {
          this.logBlockedAttempt(scriptOrigin, url);
          throw new Error(
            `ServiceWorker registration blocked: Suspicious script path ${url}`
          );
        }

        console.log(`[SW Security] Allowing registration from ${scriptOrigin}`);
        return originalRegister.call(
          navigator.serviceWorker,
          scriptURL,
          options
        );
      } catch (error) {
        console.error('[SW Security] Registration error:', error);
        throw error;
      }
    };
  }

  private isOriginAllowed(origin: string): boolean {
    return this.allowedOrigins.has(origin);
  }

  private isScriptPathSafe(scriptPath: string): boolean {
    const suspiciousPatterns = [
      /\.\.\//,
      /[<>"]/,
      /javascript:/i,
      /data:/i,
      /^\/\/(?!localhost|127\.0\.0\.1)/,
    ];

    return !suspiciousPatterns.some((pattern) => pattern.test(scriptPath));
  }

  private logBlockedAttempt(origin: string, scriptURL: string): void {
    const attempts = this.blockedAttempts.get(origin) || 0;
    this.blockedAttempts.set(origin, attempts + 1);

    console.error(`[SW Security] BLOCKED ServiceWorker registration attempt:`, {
      origin,
      scriptURL,
      timestamp: new Date().toISOString(),
      attemptCount: attempts + 1,
    });

    if (window.gtag) {
      window.gtag('event', 'security_sw_blocked', {
        event_category: 'security',
        event_label: origin,
        value: attempts + 1,
      });
    }
  }

  private async monitorServiceWorkers(): Promise<void> {
    if (!('serviceWorker' in navigator)) return;

    setInterval(async () => {
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();

        for (const registration of registrations) {
          const scope = new URL(registration.scope).origin;

          if (!this.isOriginAllowed(scope)) {
            console.warn(
              `[SW Security] Unregistering unauthorized ServiceWorker:`,
              registration.scope
            );
            await registration.unregister();
            this.logBlockedAttempt(scope, registration.scope);
          }
        }
      } catch (error) {
        console.error('[SW Security] Monitoring error:', error);
      }
    }, 30000);
  }

  public addAllowedOrigin(origin: string): void {
    this.allowedOrigins.add(origin);
  }

  public getBlockedAttempts(): Map<string, number> {
    return new Map(this.blockedAttempts);
  }

  public clearBlockedAttempts(): void {
    this.blockedAttempts.clear();
  }
}

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}
